import React, { useContext, useEffect, useState } from "react";
import { LogoGallery, PageLayout, Link, DownloadLink, useTranslator, usePathFactory } from "@sia/style-guide";
import { useTranslation } from "react-i18next";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import SideBarWithLinks from "components/SideBarWithLinks.js";
import LinkToLogin from "components/LinkToLogin";
import LinkToContactUs from "components/LinkToContactUs";
import GlobalContext from "contexts/GlobalContext";
import Page from "../Page";
import "./LandingPage.scss";
import MasterKeyModal from "../MasterKey/MasterKeyModal";
import ExplanatoryVideo from "components/Media/ExplanatoryVideo";
import HomePageParagraph from "./utils/HomePageParagraph";
import getHomePageTitle from "./utils/HomePageTitle";
import SurveyFinishedView from "./views/SurveyFinishedView";

const translationKeyPrefix = "benchmarking.data-collection-page";

const HomePage = () => {
  const { isLoaderShown, survey, language, user, loginUrl } = useContext(GlobalContext);
  const { i18n } = useTranslation();
  const pathFactory = usePathFactory();
  const surveyUrl = pathFactory.create("survey");
  const translate = useTranslator();
  const isLoggedIn = KeycloakIDPAdapter.isLoggedIn();
  const isInputUser = !!sessionStorage.getItem("sia.benchmarking.role-input");

  const today = new Date();
  const surveyIsActive = new Date(survey.activatedAt) < today && new Date(survey.toClose) > today;
  const onRevisionPhase = new Date(survey.toClose) < today && (survey.finishedAt == null || new Date(survey.finishedAt) > today);

  const isSurveyFinished = !!survey.finishedAt;

  const pageTitle = getHomePageTitle(translate, surveyIsActive, onRevisionPhase, isSurveyFinished);


  const participateLink = surveyIsActive ? (
    <DownloadLink
      openInCurrentTab
      icon="INTERNAL"
      variant="INTERNAL"
      href={surveyUrl}
      text={translate("benchmarking.landing-page.logged-in.participate.text")}
    ></DownloadLink>
  ) : onRevisionPhase ? (
    <DownloadLink
      openInCurrentTab
      icon="INTERNAL"
      variant="INTERNAL"
      href={surveyUrl}
      text={translate("benchmarking.landing-page.logged-in.closed-survey.revise.input")}
    ></DownloadLink>
  ) : null;

  const registerUrl = `${loginUrl}#/register`;
  const createRegisterUrl = () => {
    const loginUrl = `${KeycloakIDPAdapter.createLoginUrl(i18n.language, null, true)}`;
    KeycloakIDPAdapter.logout(loginUrl, true);
  };

  const [isSingleMember, setIsSingleMember] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.isMemberTypeGroupOf("single")) {
        setIsSingleMember(true);
      }
    }
  }, [user]);

  const conditionallyRenderPageContent = () => {
    if (!isLoaderShown && isLoggedIn) {
      if (isSingleMember) {
        return (
          <>
            <p className="sia-text-10">{translate("benchmarking.landing-page.logged-in.lead")}</p>
            {/* <p className="sia-text-04">{pageParagraph}</p> */}
            {/* {pageParagraph} */}
            <HomePageParagraph
              translate={translate}
              survey={survey}
              surveyIsActive={surveyIsActive}
              onRevisionPhase={onRevisionPhase}
              isSurveyFinished={isSurveyFinished}
            />
            <p className="sia-text-10">{translate("benchmarking.landing-page.user.single.paragraph-1")}</p>
            <p className="sia-text-10">{translate("benchmarking.landing-page.user.single.paragraph-2")}</p>
            {/* @ts-ignore */}
            <Link withLeadIcon type="internal" target={createRegisterUrl}>
              {translate("benchmarking.landing-page.register.text")}
            </Link>
            <p className="sia-text-10">{translate("benchmarking.landing-page.user.no-input-role")}</p>
          </>
        );
      }
      if (!isInputUser) {
        // Shown when the user does not have the input role on their token.
        return (
          <>
            <p className="sia-text-04">{translate("benchmarking.landing-page.logged-in.paragraph", { surveyYear: survey.surveyYear })}</p>
            <p className="sia-text-10">{translate("benchmarking.landing-page.user.no-input-role.activation")}</p>
            <DownloadLink
              openInCurrentTab
              icon="INTERNAL"
              variant="EXTERNAL"
              href={`${process.env.REACT_APP_WEB_PLATFORM_URL}/${language}/account/services`}
              text={translate("benchmarking.landing-page.user.no-input-role.contact")}
            ></DownloadLink>
            <p className="sia-text-10">{translate("benchmarking.landing-page.user.no-input-role")}</p>
            <p className="sia-text-10">{translate("benchmarking.landing-page.user.no-input-role.extra")}</p>
          </>
        );
      }
      if (isSurveyFinished) {
        // Shown when user is logged in, survey is active has the role and can input. The happy path
        return (
          <SurveyFinishedView
            translate={translate}
            survey={survey}
            surveyIsActive={surveyIsActive}
            onRevisionPhase={onRevisionPhase}
            isSurveyFinished={isSurveyFinished}
            surveyUrl={surveyUrl}
          />
        );
      } else if (surveyIsActive || onRevisionPhase) {
        // Shown when user is logged in, survey is active has the role and can input. The happy path
        return (
          <>
            <p className="sia-text-10">{translate("benchmarking.landing-page.logged-in.lead")}</p>
            <HomePageParagraph
              translate={translate}
              survey={survey}
              surveyIsActive={surveyIsActive}
              onRevisionPhase={onRevisionPhase}
              isSurveyFinished={isSurveyFinished}
            />
            {participateLink}
            <p className="sia-text-10">{translate("benchmarking.landing-page.logged-in.paragraph-2")}</p>
            <p className="sia-text-10">{translate("benchmarking.landing-page.logged-in.paragraph-3")}</p>
          </>
        );
      }
      // Shown  when user is logged in and survey season inactive
      else
        return (
          <>
            {/* Shown when survey season inactive */}
            <p className="sia-text-10">{translate(`${translationKeyPrefix}.inactive.sub-lead`)}</p>
            <p className="sia-text-10">{translate(`${translationKeyPrefix}.inactive.sub-lead-2`, { link: "www.web-sia.ch" })}</p>
            <LinkToContactUs translate={translate} translationKeyPrefix={translationKeyPrefix} />
          </>
        );
    }

    // Shown to a user who isn't logged in
    return (
      <>
        <p className="sia-text-10">{translate("benchmarking.landing-page.lead")}</p>
        <ExplanatoryVideo />
        {/* <p className="sia-text-04">{pageParagraph}</p> */}
        <HomePageParagraph
          translate={translate}
          survey={survey}
          surveyIsActive={surveyIsActive}
          onRevisionPhase={onRevisionPhase}
          isSurveyFinished={isSurveyFinished}
        />
        <p className="sia-text-10">{translate("benchmarking.landing-page.paragraph-2")}</p>
        <LinkToLogin />
        <DownloadLink
          openInCurrentTab
          icon="INTERNAL"
          variant="EXTERNAL"
          href={registerUrl}
          text={translate("benchmarking.landing-page.register.text")}
        ></DownloadLink>
        <p className="sia-text-04">{translate("benchmarking.landing-page.paragraph-3")}</p>
        <p className="sia-text-10">{translate("benchmarking.landing-page.paragraph-4")}</p>
        <p className="sia-text-10">{translate("benchmarking.landing-page.paragraph-5")}</p>
      </>
    );
  };

  return (
    <Page className="landing" title={pageTitle}>
      <PageLayout variant="layout_25_50_25">
        <div></div>
        <div className="center">
          <h2>{pageTitle}</h2>
          {conditionallyRenderPageContent()}
        </div>
        <div className={`right-column ${pageTitle.length > 39 ? "double-height-title" : ""}`}>
          <SideBarWithLinks />
        </div>
      </PageLayout>

      <LogoGallery
        items={[
          { src: "/images/partner-logos/bsa.jpg", description: "BSA", link: "https://www.bsa-fas.ch", customStyle: { height: 36 } },
          { src: "/images/partner-logos/bsla-fsap.jpg", description: "BSLA-FSAP", link: "http://www.bsla.ch", customStyle: { height: 53 } },
          { src: "/images/partner-logos/igs.jpg", description: "IGS", link: "https://www.igs-ch.ch/", customStyle: { height: 44 } },
          { src: "/images/partner-logos/svu.jpg", description: "SVU", link: "https://www.svu-asep.ch/", customStyle: { height: 50, width: 350 } },
          { src: "/images/partner-logos/fsai.jpg", description: "FSAI", link: "https://www.fsai.ch/", customStyle: { height: 62, width: 350 } },
          { src: "/images/partner-logos/fsu.jpg", description: "FSU", link: "https://www.f-s-u.ch/", customStyle: { height: 50 } },
          { src: "/images/partner-logos/suisseing.jpg", description: "SUISSEING", link: "https://www.suisse.ing/", customStyle: { height: 78 } },
        ]}
        translate={translate}
      />
      {isLoggedIn && isSingleMember ? null : <MasterKeyModal translate={translate} />}
    </Page>
  );
};

export default HomePage;
